/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global window */

import { Shape, GraphUtils, CaptionNode, StreamSheetContainer, ChangeItemOrderCommand } from '@cedalo/jsg-core';
import JSG from '../../../index';
import StreamSheetContainerView from '../view/StreamSheetContainerView';
import InteractionActivator from './InteractionActivator';
import Cursor from '../../ui/Cursor';

const KEY = 'caption.activator';

export default class CaptionActivator extends InteractionActivator {
	getKey() {
		return CaptionActivator.KEY;
	}

	/**
	 * Gets the controller at specified location or <code>undefined</code> if none could be found.
	 *
	 * @method _getControllerAt
	 * @param {Point} location The location, relative to Graph coordinate system, to start look up at.
	 * @param {InteractionDispatcher} dispatcher The InteractionDispatcher which
	 * notified this activator.
	 * @return {GraphItemController} The controller at specified location or
	 * <code>undefined</code>.
	 */
	_getControllerAt(location, viewer, dispatcher) {
		return viewer.filterFoundControllers(Shape.FindFlags.AREA, (cont) => {
			if (!(cont.getModel() instanceof CaptionNode)) {
				return false;
			}
			return true;
		});
	}

	/**
	 * Handles mouse up events.</br>
	 *
	 * @method onMouseDoubleClick
	 * @param {MouseEvent} event The mouse up event.
	 * @param {ControllerViewer} viewer The ControllerViewer used by
	 * {{#crossLink "InteractionHandler"}}{{/crossLink}}.
	 * @param {InteractionDispatcher} dispatcher The InteractionDispatcher which
	 * notified this activator.
	 */
	onMouseDoubleClick(event, viewer, dispatcher) {
		const controller = this._getControllerAt(event.location, viewer, dispatcher);
		if (controller && controller.getModel().getType().getValue() === 'inboxcaption') {
			JSG.NotificationCenter.getInstance().send(new JSG.Notification('ShowInboxSettings'));
			event.isConsumed = true;
			event.hasActivated = true;
		}
	}

	onMouseDown(event, viewer, dispatcher) {
		const controller = this._getControllerAt(event.location, viewer, dispatcher);
		if (controller && controller.getModel().getType().getValue() === 'inboxcaption') {
			let sheetContainerView = controller.getView();

			while (sheetContainerView && !(sheetContainerView instanceof StreamSheetContainerView)) {
				sheetContainerView = sheetContainerView.getParent();
			}

			if (
				sheetContainerView !== undefined &&
				sheetContainerView._subviews.length > 3 &&
				sheetContainerView._subviews[3].getItem().getItemType() === 'processsheet'
			) {
				sheetContainerView._subviews[3].notifySelectionChange(viewer);
			}

			JSG.NotificationCenter.getInstance().send(new JSG.Notification('ShowInboxSettings'));
			event.isConsumed = true;
			event.hasActivated = true;
		}
	}

	onMouseMove(event, viewer, dispatcher) {
		const controller = this._getControllerAt(event.location, viewer, dispatcher);
		if (controller && controller.getModel().getType().getValue() === 'inboxcaption') {
			const sheet = controller.getModel().getParent().streamSheet;
			if (!sheet.isProtected()) {
				dispatcher.setCursor(Cursor.Style.EXECUTE);
				event.isConsumed = true;
				event.hasActivated = true;
			}
		}
	}

	static get KEY() {
		return KEY;
	}
}
